'use client';

import type { FC, ReactNode } from 'react';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { useNavigation } from '../../contexts/NavigationContext';
import { ScreenContent, ScreenFixedTop, ScreenFixedBottom } from '../../screen';
import { Spinner } from '../../components/ui/Spinner';
import { Skeleton } from '../../components/ui/Skeleton';
import { ErrorMessage } from '../../components/ErrorMessage';
import { Button, ButtonGroup, EmptyState, SegmentedControl } from '../../components/ui';
import type { Segment } from '../../components/ui';
import { BattleCard, BattleCardSkeleton } from '../../components/BattleCard';
import { BattleRow, BattleRowHeader } from '../../components/BattleCard/BattleRow';
import { DesktopFullBleed } from '../../contexts/DesktopContentWidthContext';
import { AnimatedBattleList } from './components/AnimatedBattleList';
import { useBattleTab, type BattleFilter } from './useBattleTab';
import { useCaseBattleSubscription } from '../../hooks/useCaseBattleSubscription';
import { useTouchFrozen } from '../../components/caseBattle/useTouchFrozen';
import { useCaseBattleT } from '../../components/caseBattle/atoms';
import { CaseBattleSettingsSheet } from '../../components/caseBattle/CaseBattleSettingsSheet';
import { RiSettings3Line } from '../../components/icons';
import { cn } from '../../utils/cn';
import { toast } from 'sonner';
import { ERROR_TOAST_MS, Toast, ToastAlert, showCustomToast } from '../../components/Toast';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { useIsAuthed } from '../../hooks/useIsAuthed';
import { useProfile } from '../../queries/useProfileQueries';
import { useJoinCaseBattle } from '../../queries/useCaseBattleQueries';
import { useHaptic } from '../../hooks/useHaptic';
import { formatApiError } from '../../utils/apiError';
import { JoinConfirmSheet } from '../BattleDetailsScreen/components/sheets/BattleSheets';
import { resolveJoinFailure } from '../BattleDetailsScreen/battleActionError';
import type { BattleData } from '../../components/BattleCard/BattleCard';
import detailsTranslations from '../BattleDetailsScreen/BattleDetailsScreen.translations';
import { LoginMethods } from '../../components/LoginMethods';
import { isBandFunded } from '../../components/caseBattle/battleModel';
import translations from './CaseBattlesScreen.translations';

interface CaseBattlesScreenProps {
  params?: { filter?: BattleFilter };
  onBack?: () => void;
  seoSlot?: ReactNode;
}

const BattleListSkeleton: FC<{ rows: boolean }> = ({ rows }) => (
  <div className="flex flex-col gap-2">
    {Array.from({ length: 4 }).map((_, i) => (rows ? <Skeleton key={i} className="h-[88px] rounded-2xl" /> : <BattleCardSkeleton key={i} />))}
  </div>
);

export const CaseBattlesScreen: FC<CaseBattlesScreenProps> = ({ params, seoSlot }) => {
  const { t } = useLocalTranslation(translations);
  const navigation = useNavigation();
  const isDesktop = useIsDesktop();
  const isAuthed = useIsAuthed();
  const [filter, setFilter] = useState<BattleFilter>(params?.filter ?? 'active');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { t: tBattle } = useCaseBattleT();

  const paramsFilter = params?.filter;
  useEffect(() => {
    if (paramsFilter && paramsFilter !== filter) {
      setFilter(paramsFilter);
    }
  }, [paramsFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useCaseBattleSubscription(undefined);

  const tab = useBattleTab(filter);
  const { now, filteredOut, isLoading, error, refetch, hasMore, isFetchingMore, fetchNextPage } = tab;
  const { items: frozenBattles, onTouchStart, onTouchEnd } = useTouchFrozen(tab.items);
  // WHY: баттл хедлайнеров — событие клуба на всю неделю, в лобби он закреплён первым; в остальном это обычный баттл ленты
  const battles = useMemo(
    () =>
      filter === 'active' && frozenBattles
        ? [...frozenBattles.filter(isBandFunded), ...frozenBattles.filter((b) => !isBandFunded(b))]
        : frozenBattles,
    [frozenBattles, filter],
  );

  const tabs: Segment<BattleFilter>[] = [
    { id: 'active', label: t('tabActive') },
    { id: 'historyAll', label: t('tabHistoryAll') },
    { id: 'historyMine', label: t('tabHistoryMine') },
  ];

  const scrollRef = useRef<HTMLDivElement | null>(null);
  useInfiniteScroll({
    hasMore,
    loading: isFetchingMore,
    onLoadMore: fetchNextPage,
    threshold: 200,
    scrollRef,
  });

  const handleFilterChange = useCallback((next: BattleFilter) => {
    setFilter(next);
    navigation.updateCurrentParams({ filter: next });
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [navigation]);

  const staleWithError = Boolean(error) && (battles?.length ?? 0) > 0;
  useEffect(() => {
    if (!staleWithError) return;
    showCustomToast(
      (toastId) => (
        <Toast
          leading={<ToastAlert />}
          title={t('refreshFailed')}
          description={t('retry')}
          onClick={() => refetch()}
          onDismiss={() => toast.dismiss(toastId)}
        />
      ),
      { id: 'case-battles-refresh', duration: ERROR_TOAST_MS },
    );
  }, [staleWithError, refetch, t]);

  // WHY: рефетч на возврат на таб, не на маунт — на маунте выбрасывает SSR-гидрированное лобби
  const prevTabRef = useRef(filter);
  useEffect(() => {
    if (prevTabRef.current === filter) return;
    prevTabRef.current = filter;
    refetch();
  }, [filter, refetch]);

  const handleBattleClick = useCallback((battleId: string) => {
    navigation.pushScreen('games', 'case-battle-details', { battleId });
  }, [navigation]);

  const { t: tDetails } = useLocalTranslation(detailsTranslations);
  const { data: profile } = useProfile();
  const joinBattle = useJoinCaseBattle();
  const haptic = useHaptic();
  const [joinTarget, setJoinTarget] = useState<BattleData | null>(null);
  const balance = profile?.balanceInCredits ?? null;
  const joinableIds = new Set(
    balance === null
      ? []
      : (battles ?? [])
          .filter((b) => b.status === 'WAITING' && !b.mine && b.currentPlayers < b.maxPlayers && b.totalCost <= balance)
          .map((b) => b.id),
  );
  const handleJoinFromCard = useCallback(
    (battleId: string) => {
      const target = battles?.find((b) => b.id === battleId) ?? null;
      setJoinTarget(target);
    },
    [battles],
  );
  const closeJoin = useCallback(() => setJoinTarget(null), []);
  const handleJoinConfirm = useCallback(
    async (slot: number | undefined) => {
      if (!joinTarget) return;
      const battleId = joinTarget.id;
      haptic.impact('medium');
      try {
        await joinBattle.mutateAsync({ battleId, slot });
        setJoinTarget(null);
        navigation.pushScreen('games', 'case-battle-details', { battleId });
      } catch (err) {
        haptic.notification('error');
        console.error('Failed to join case battle:', formatApiError(err));
        setJoinTarget(null);
        const failure = resolveJoinFailure(err);
        if (failure.kind === 'tradeLink') {
          navigation.navigate('trade-link', { currentTradeLink: profile?.steamTradeUrl || '' });
          return;
        }
        showCustomToast(
          (toastId) => (
            <Toast
              leading={<ToastAlert />}
              title={failure.kind === 'message' ? tDetails(failure.key) : tDetails('errorJoining')}
              onDismiss={() => toast.dismiss(toastId)}
            />
          ),
          { id: 'case-battles-join', duration: ERROR_TOAST_MS },
        );
      }
    },
    [joinTarget, joinBattle, haptic, navigation, profile?.steamTradeUrl, tDetails],
  );

  const handleCreateClick = () => {
    navigation.pushScreen('games', 'case-battle-create');
  };

  const showHistoryMineLoginWall = filter === 'historyMine' && !isAuthed;
  const isLobby = filter === 'active';

  const renderEmpty = () => {
    if (!isLobby) return <EmptyState message={t('emptyHistory')} />;
    if (filteredOut) return <EmptyState title={tBattle('emptyFiltered')} description={tBattle('emptyFilteredHint')} />;
    return <EmptyState title={t('empty')} description={t('emptyHint')} />;
  };

  const battleListContent = showHistoryMineLoginWall ? (
    <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
      <p className="text-base text-white/70 mb-6 max-w-[320px] leading-snug">
        {t('historyMineLoginReason')}
      </p>
      <LoginMethods />
    </div>
  ) : (
    <>
      {isLoading && !battles?.length ? (
        <BattleListSkeleton rows={isDesktop} />
      ) : error && (!battles || battles.length === 0) ? (
        <ErrorMessage onRetry={() => refetch()} />
      ) : (
        <div className="flex flex-col gap-2 min-w-0" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} onTouchCancel={onTouchEnd}>
          {isDesktop && (battles?.length ?? 0) > 0 && <BattleRowHeader />}
          <AnimatedBattleList
            key={filter}
            battles={battles}
            renderBattle={(battle) => {
              const Item = isDesktop ? BattleRow : BattleCard;
              return (
                <Item
                  battle={battle}
                  now={now}
                  variant={isLobby ? 'lobby' : 'history'}
                  onOpen={handleBattleClick}
                  onJoin={joinableIds.has(battle.id) ? handleJoinFromCard : undefined}
                />
              );
            }}
            renderEmpty={renderEmpty}
          />
          {isFetchingMore && (
            <div className="flex justify-center py-4">
              <Spinner size="s" />
            </div>
          )}
        </div>
      )}
    </>
  );

  const settingsButton = (
    <button
      type="button"
      aria-label={tBattle('settingsTitle')}
      onClick={() => setSettingsOpen(true)}
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full bg-bg-secondary text-label-primary active:opacity-70',
        isDesktop ? 'size-9' : 'size-[50px]',
      )}
    >
      <RiSettings3Line size={isDesktop ? 18 : 24} />
    </button>
  );
  const settingsSheet = <CaseBattleSettingsSheet isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} />;
  const joinSheet = joinTarget && (
    <JoinConfirmSheet isOpen onClose={closeJoin} battle={joinTarget} initialSlot={null} pending={joinBattle.isPending} onConfirm={handleJoinConfirm} />
  );

  if (isDesktop) {
    return (
      <ScreenContent scrollRef={scrollRef} scrollKey="case-battles">
        <DesktopFullBleed />
        <div className="flex flex-col gap-4 p-6">
          <div className="flex items-center justify-between gap-4">
            <SegmentedControl segments={tabs} value={filter} onChange={handleFilterChange} className="w-[420px]" />
            <div className="flex items-center gap-2">
              {settingsButton}
              <Button size="m" variant="cta" onClick={handleCreateClick} className="px-5">
                {t('createBattle')}
              </Button>
            </div>
          </div>
          {battleListContent}
        </div>
        {seoSlot && <div className="px-6 pb-8">{seoSlot}</div>}
        {settingsSheet}
        {joinSheet}
      </ScreenContent>
    );
  }

  return (
    <>
      <ScreenFixedTop title={t('title')}>
        <SegmentedControl segments={tabs} value={filter} onChange={handleFilterChange} className="px-4 py-2" />
      </ScreenFixedTop>
      <ScreenContent scrollRef={scrollRef} scrollKey="case-battles">
        <div className="p-4">
          {battleListContent}
        </div>
        {seoSlot && <div className="px-4 pb-8">{seoSlot}</div>}
      </ScreenContent>
      <ScreenFixedBottom>
        <ButtonGroup direction="horizontal">
          <Button size="l" stretched variant="cta" onClick={handleCreateClick}>
            {t('createBattle')}
          </Button>
          {settingsButton}
        </ButtonGroup>
      </ScreenFixedBottom>
      {settingsSheet}
      {joinSheet}
    </>
  );
};
